import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useSWR, { mutate } from 'swr'
import { asLeadId, asPipelineId, type Lead, type LeadId, type PipelineId } from '@ludus/core'
import { shortLeadId } from '../utils/format.ts'
import { Breadcrumb } from '@/shared/breadcrumb/Breadcrumb.tsx'
import { CenteredPanel } from '@/shared/feedback/CenteredPanel.tsx'
import {
  fetchLead,
  fetchPipeline,
  fetchPipelineManagers,
  fetchPipelineStatuses,
  PipelineApiError,
} from '@/lib/api/pipelines.ts'
import { fetchContact } from '@/lib/api/contacts.ts'
import { LeadDetailHeader } from '../components/LeadDetailHeader.tsx'
import { ValueSummaryStrip } from '../components/ValueSummaryStrip.tsx'
import { StageProgressBar } from '../components/StageProgressBar.tsx'
import { LeadTabsShell } from '../components/LeadTabsShell.tsx'
import { LeadPropertiesPanel } from '../components/LeadPropertiesPanel.tsx'
import { ContactCard } from '../components/ContactCard.tsx'
import { CompanyPlaceholder } from '../components/CompanyPlaceholder.tsx'

/**
 * Lead detail subroute (SBO-47). Reachable from the kanban (lead card click)
 * and from SBO-53's quick-create redirect. Inline edits + stage move (SBO-57)
 * write through `updateLead`; the server's returned lead is pushed straight
 * into the `leadSwr` cache, so the hero / strip / progress bar re-render.
 *
 * SWR composition: 4 page-level fetches (lead/pipeline/statuses/managers)
 * block the layout; the optional 5th (contact) is keyed on `lead.contactId`
 * and lives inside the `<ContactCard>`. The page-level guards mirror SBO-53
 * placeholder semantics — invalid URL, loading, error, no-lead.
 */
export function LeadDetailPage() {
  const { t } = useTranslation()
  const params = useParams<{ pipelineId: string; leadId: string }>()

  const pipelineId = params.pipelineId ? asPipelineId(params.pipelineId) : null
  const leadId = params.leadId ? asLeadId(params.leadId) : null

  const leadSwr = useSWR(
    pipelineId && leadId ? `/pipelines/${pipelineId}/leads/${leadId}` : null,
    () => fetchLead(pipelineId as PipelineId, leadId as LeadId),
  )
  const pipelineSwr = useSWR(pipelineId ? `/pipelines/${pipelineId}` : null, () =>
    fetchPipeline(pipelineId as PipelineId),
  )
  const statusesSwr = useSWR(pipelineId ? `/pipelines/${pipelineId}/statuses` : null, () =>
    fetchPipelineStatuses(pipelineId as PipelineId),
  )
  const managersSwr = useSWR(pipelineId ? `/pipelines/${pipelineId}/managers` : null, () =>
    fetchPipelineManagers(pipelineId as PipelineId),
  )
  const lead = leadSwr.data
  const contactSwr = useSWR(lead?.contactId ? `/contacts/${lead.contactId}` : null, () =>
    fetchContact(lead!.contactId!),
  )

  if (!pipelineId || !leadId) {
    return (
      <CenteredPanel
        title={t('pages.leads.detail.invalid.title', { defaultValue: 'Invalid URL' })}
        body={t('pages.leads.detail.invalid.body', {
          defaultValue: 'The lead URL is missing required identifiers.',
        })}
      />
    )
  }

  if (
    leadSwr.isLoading ||
    pipelineSwr.isLoading ||
    statusesSwr.isLoading ||
    managersSwr.isLoading
  ) {
    return (
      <CenteredPanel
        title={t('pages.leads.detail.loading', { defaultValue: 'Loading lead…' })}
        muted
      />
    )
  }

  const error =
    (leadSwr.error as PipelineApiError | undefined) ??
    (pipelineSwr.error as PipelineApiError | undefined) ??
    (statusesSwr.error as PipelineApiError | undefined) ??
    (managersSwr.error as PipelineApiError | undefined)
  if (error) {
    return (
      <CenteredPanel
        title={t('pages.leads.detail.error.title', { defaultValue: 'Could not load lead' })}
        body={
          error.kind === 'not_found'
            ? t('pages.leads.detail.error.notFound', {
                defaultValue: 'This lead is no longer available in the connected pipeline.',
              })
            : t('pages.leads.detail.error.generic', {
                defaultValue: 'The engine returned an error while loading this lead.',
              })
        }
        meta={error.status !== null ? `HTTP ${error.status} · ${error.message}` : undefined}
        tone="error"
      />
    )
  }
  if (!lead || !pipelineSwr.data || !statusesSwr.data || !managersSwr.data) {
    return null
  }

  const pipeline = pipelineSwr.data
  const statuses = statusesSwr.data
  const managers = managersSwr.data
  // A successful write returns the canonical lead; seed it into the single-lead
  // cache without a revalidation round-trip (mirrors `populateCache` semantics).
  // The kanban board reads different keys (`['leads', pid, query]` and
  // `['leadStats', pid, filters]`), so a stage move / inline edit leaves those
  // stale — invalidate them for this pipeline so the board (list + dashboard)
  // refetches on next focus. No data arg → revalidate; no optimistic update.
  const onSaved = (updated: Lead) => {
    void leadSwr.mutate(updated, { revalidate: false })
    void mutate(
      (key) =>
        Array.isArray(key) &&
        key[1] === pipelineId &&
        (key[0] === 'leads' || key[0] === 'leadStats'),
    )
  }
  const stage = statuses.find((s) => s.id === lead.statusId) ?? null
  const owner =
    lead.assignedManagerId === null
      ? null
      : (managers.find((m) => m.id === lead.assignedManagerId) ?? null)

  return (
    <div className="flex w-full flex-1 flex-col gap-4 p-4 sm:p-6">
      <Breadcrumb
        items={[
          {
            label: t('pages.leads.detail.breadcrumb.pipelines', { defaultValue: 'Pipelines' }),
            to: '/pipelines',
          },
          { label: pipeline.name, to: `/pipelines/${pipelineId}` },
          lead.name ? { label: lead.name } : { label: shortLeadId(lead.id), mono: true },
        ]}
      />
      <section className="bg-gradient-hero-detail flex flex-col gap-4 rounded-xl border border-primary/20 p-4 sm:gap-6 sm:p-6">
        <LeadDetailHeader
          lead={lead}
          statuses={statuses}
          pipelineId={pipelineId}
          onSaved={onSaved}
        />
        <ValueSummaryStrip lead={lead} stage={stage} owner={owner} />
        <StageProgressBar statuses={statuses} currentStatusId={lead.statusId} />
      </section>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[380px_minmax(0,1fr)]">
        <aside className="flex flex-col gap-4">
          <LeadPropertiesPanel
            lead={lead}
            stage={stage}
            owner={owner}
            pipelineId={pipelineId}
            managers={managers}
            onSaved={onSaved}
          />
          <ContactCard
            hasContactRef={lead.contactId !== null}
            contact={contactSwr.data}
            isLoading={contactSwr.isLoading}
            error={contactSwr.error as PipelineApiError | undefined}
          />
          <CompanyPlaceholder />
        </aside>
        <LeadTabsShell
          pipelineId={pipelineId}
          leadId={leadId}
          externalRef={lead.externalRef}
          ludusLeadId={lead.ludusLeadId ?? null}
        />
      </div>
    </div>
  )
}
